/* ── Birch Tracker Caregivers — shared invite + access module ────────────── */
'use strict';

(function () {
  var API = '/apps/ai/tracker-caregivers';

  var TRACKER_LABELS = {
    'feeding':  'NurseNote (Feeding)',
    'sleep':    'DreamLog (Sleep)',
    'baby-log': 'BabyLog (Daily Care)',
  };

  /* ── Low-level API helpers ─────────────────────────────────────────────── */
  function apiFetch(path, options) {
    return fetch(API + path, Object.assign({ credentials: 'same-origin', headers: { 'Content-Type': 'application/json' } }, options || {}))
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, status: r.status, data: d }; }); });
  }

  /* ── State ─────────────────────────────────────────────────────────────── */
  var _invites = null;     // { sent: [], received: [] } — loaded once per page
  var _loadPromise = null;

  function loadInvites() {
    if (_loadPromise) return _loadPromise;
    _loadPromise = apiFetch('/invites').then(function (r) {
      if (r.ok) _invites = r.data;
      return _invites;
    }).catch(function () { return null; });
    return _loadPromise;
  }

  /* ── Caregiver mode detection ──────────────────────────────────────────── */
  // Returns the ownerId this caregiver is viewing, or null if not in caregiver mode.
  function getCaregiverOwner(trackerId) {
    var params = new URLSearchParams(window.location.search);
    var ownerParam = params.get('owner');
    if (!ownerParam) return null;
    // Verify in received list
    if (_invites) {
      var match = _invites.received.find(function (r) {
        return r.ownerId === ownerParam && r.trackers.indexOf(trackerId) !== -1;
      });
      return match ? ownerParam : null;
    }
    return ownerParam; // optimistic — verified server-side on data fetch
  }

  /* ── Data sync ─────────────────────────────────────────────────────────── */
  // Fetch owner's tracker data from worker (returns null if not available).
  function fetchTrackerData(trackerId, ownerId) {
    var path = '/data/' + trackerId + (ownerId ? '?ownerId=' + encodeURIComponent(ownerId) : '');
    return apiFetch(path).then(function (r) { return r.ok ? r.data : null; });
  }

  // Push tracker data to worker (owner or caregiver writing for owner).
  function pushTrackerData(trackerId, payload, ownerId) {
    var path = '/data/' + trackerId + (ownerId ? '?ownerId=' + encodeURIComponent(ownerId) : '');
    return apiFetch(path, { method: 'POST', body: JSON.stringify(payload) });
  }

  /* ── Panel UI ──────────────────────────────────────────────────────────── */
  function buildPanel(trackerId) {
    var el = document.createElement('div');
    el.id = 'tc-panel';
    el.className = 'tc-panel';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-label', 'Manage caregivers');
    el.innerHTML = [
      '<div class="tc-panel__backdrop" id="tc-backdrop"></div>',
      '<div class="tc-panel__sheet">',
      '  <div class="tc-panel__head">',
      '    <span class="tc-panel__title">Caregivers</span>',
      '    <button class="tc-panel__close" id="tc-close" aria-label="Close">✕</button>',
      '  </div>',
      '  <div class="tc-panel__body" id="tc-body">',
      '    <div class="tc-spinner" id="tc-loading"><span></span></div>',
      '  </div>',
      '</div>',
    ].join('');
    document.body.appendChild(el);

    el.querySelector('#tc-close').addEventListener('click', closePanel);
    el.querySelector('#tc-backdrop').addEventListener('click', closePanel);

    loadInvites().then(function () { renderPanelBody(trackerId); });
    return el;
  }

  function closePanel() {
    var panel = document.getElementById('tc-panel');
    if (panel) { panel.classList.remove('tc-panel--open'); setTimeout(function () { panel.remove(); }, 280); }
  }

  function openPanel(trackerId) {
    var existing = document.getElementById('tc-panel');
    if (existing) existing.remove();
    var panel = buildPanel(trackerId);
    requestAnimationFrame(function () { panel.classList.add('tc-panel--open'); });
  }

  function renderPanelBody(trackerId) {
    var body = document.getElementById('tc-body');
    if (!body) return;
    var sent = (_invites && _invites.sent) ? _invites.sent.filter(function (s) { return s.trackers.indexOf(trackerId) !== -1; }) : [];

    var html = '<p class="tc-section-label">Share this tracker</p>';
    html += '<div class="tc-share-form" id="tc-share-form">';
    html += '<p class="tc-share-desc">Invite a partner, grandparent, or caregiver to log entries with you.</p>';
    html += '<button class="tc-btn tc-btn--primary" id="tc-gen-link">Generate invite link</button>';
    html += '</div>';

    if (sent.length > 0) {
      html += '<p class="tc-section-label" style="margin-top:20px">Active caregivers</p>';
      html += '<ul class="tc-list" id="tc-caregiver-list">';
      sent.forEach(function (s) {
        var name = s.caregiverName || s.caregiverEmail || 'Pending acceptance';
        var badge = s.status === 'accepted' ? '<span class="tc-badge tc-badge--green">Active</span>' : '<span class="tc-badge">Pending</span>';
        html += '<li class="tc-list-item">';
        html += '<div class="tc-list-item__main"><span class="tc-list-item__name">' + escHtml(name) + '</span>' + badge + '</div>';
        html += '<button class="tc-list-item__revoke" data-token="' + escHtml(s.token) + '">Remove</button>';
        html += '</li>';
      });
      html += '</ul>';
    }

    body.innerHTML = html;

    document.getElementById('tc-gen-link').addEventListener('click', function () {
      generateInvite(trackerId);
    });
    body.querySelectorAll('.tc-list-item__revoke').forEach(function (btn) {
      btn.addEventListener('click', function () { revokeInvite(btn.getAttribute('data-token'), trackerId); });
    });
  }

  function generateInvite(trackerId) {
    // Step 1: show email-collection form before hitting the API
    var form = document.getElementById('tc-share-form');
    if (!form) return;
    form.innerHTML = [
      '<p class="tc-share-desc">Who are you inviting? Enter their email so only they can accept.</p>',
      '<input class="tc-link-input" id="tc-invitee-email" type="email" placeholder="caregiver@email.com" style="margin-bottom:8px">',
      '<div style="display:flex;gap:8px">',
      '  <button class="tc-btn" id="tc-do-gen">Send invite link</button>',
      '  <button class="tc-btn tc-btn--ghost" id="tc-gen-cancel">Cancel</button>',
      '</div>',
      '<p id="tc-email-err" style="color:#c53030;font-size:12px;margin:6px 0 0"></p>',
    ].join('');

    document.getElementById('tc-gen-cancel').addEventListener('click', function () {
      _loadPromise = null;
      renderPanelBody(trackerId);
    });

    document.getElementById('tc-do-gen').addEventListener('click', function () {
      var emailEl = document.getElementById('tc-invitee-email');
      var errEl   = document.getElementById('tc-email-err');
      var email   = emailEl ? emailEl.value.trim() : '';
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        if (errEl) errEl.textContent = 'Please enter a valid email address.';
        return;
      }

      var btn = document.getElementById('tc-do-gen');
      if (btn) { btn.disabled = true; btn.textContent = 'Generating…'; }

      // Attach child context if available (set by the tracker JS)
      var childName = window._activeChildId && window.prefs ? (window.prefs.babyName || '') : '';
      var childId   = window._activeChildId || '';

      apiFetch('/invite', {
        method: 'POST',
        body: JSON.stringify({
          trackers:      [trackerId],
          inviteeEmail:  email,
          childName:     childName || undefined,
          childId:       childId   || undefined,
        })
      })
        .then(function (r) {
          if (!r.ok) throw new Error((r.data && r.data.error) || 'Failed');
          var link = window.location.origin + r.data.link;
          showLinkCopier(link, trackerId, email);
          _loadPromise = null;
        })
        .catch(function (err) {
          if (errEl) errEl.textContent = 'Could not generate link: ' + String(err);
          if (btn) { btn.disabled = false; btn.textContent = 'Try again'; }
        });
    });
  }

  function showLinkCopier(link, trackerId, email) {
    var form = document.getElementById('tc-share-form');
    if (!form) return;
    form.innerHTML = [
      '<p class="tc-share-desc">✅ Invite created for <strong>' + escHtml(email) + '</strong>. Share this link — only they can accept it.</p>',
      '<div class="tc-link-row">',
      '  <input class="tc-link-input" id="tc-invite-link" value="' + escHtml(link) + '" readonly>',
      '  <button class="tc-btn tc-btn--copy" id="tc-copy-btn">Copy</button>',
      '</div>',
      '<button class="tc-btn tc-btn--ghost" id="tc-new-link" style="margin-top:8px;font-size:12px">Invite someone else</button>',
    ].join('');
    form.querySelector('#tc-copy-btn').addEventListener('click', function () {
      navigator.clipboard.writeText(link).then(function () {
        var b = document.getElementById('tc-copy-btn');
        if (b) { b.textContent = 'Copied!'; setTimeout(function () { if (b) b.textContent = 'Copy'; }, 2000); }
      }).catch(function () {
        document.getElementById('tc-invite-link').select();
        document.execCommand('copy');
      });
    });
    form.querySelector('#tc-new-link').addEventListener('click', function () {
      _loadPromise = null;
      renderPanelBody(trackerId);
    });
  }

  function revokeInvite(token, trackerId) {
    apiFetch('/invite/' + token + '/revoke', { method: 'POST' })
      .then(function () {
        _loadPromise = null;
        _invites = null;
        loadInvites().then(function () { renderPanelBody(trackerId); });
      });
  }

  /* ── Caregiver banner ──────────────────────────────────────────────────── */
  function showCaregiverBanner(ownerName, trackerId) {
    var existing = document.getElementById('tc-caregiver-banner');
    if (existing) return;
    var banner = document.createElement('div');
    banner.id = 'tc-caregiver-banner';
    banner.className = 'tc-caregiver-banner';
    banner.innerHTML = [
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">',
      '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>',
      '<path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
      '</svg>',
      '<span>You are viewing <strong>' + escHtml(ownerName) + '\'s</strong> ' + escHtml(TRACKER_LABELS[trackerId] || trackerId) + '</span>',
      '<a class="tc-caregiver-banner__switch" href="' + window.location.pathname + '">Switch to your own</a>',
    ].join('');
    var first = document.querySelector('main') || document.body;
    first.prepend(banner);
  }

  /* ── Utility ───────────────────────────────────────────────────────────── */
  function escHtml(s) {
    return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }

  /**
   * getAllContexts — returns every tracker context the current user can view:
   *   - their own children (type:'own')
   *   - children shared with them via accepted caregiver invites (type:'shared')
   * Used by the context picker dropdown in tracker pages.
   *
   * @param {string} trackerId
   * @returns {Promise<TrackerContext[]>}
   */
  function getAllContexts(trackerId) {
    return loadInvites().then(function () {
      var sharedContexts = (_invites.received || [])
        .filter(function (r) { return r.trackers && r.trackers.indexOf(trackerId) !== -1; })
        .map(function (r) {
          return {
            type:      'shared',
            childName: r.childName || (r.ownerName ? r.ownerName + '\'s child' : 'Shared child'),
            childId:   r.childId   || null,
            dob:       null,
            ownerId:   r.ownerId,
            ownerName: r.ownerName,
            label:     (r.childName || 'tracker') + ' (' + (r.ownerName || 'shared') + ')',
          };
        });

      if (window.BirchChildren) {
        return window.BirchChildren.load().then(function (ownChildren) {
          var ownContexts = ownChildren.map(function (c) {
            return {
              type:      'own',
              childName: c.name,
              childId:   c.id,
              dob:       c.dob   || null,
              ownerId:   null,
              ownerName: null,
              label:     c.name,
            };
          });
          return ownContexts.concat(sharedContexts);
        });
      }
      return sharedContexts;
    });
  }

  /* ── Public API ────────────────────────────────────────────────────────── */
  window.BirchCaregivers = {
    /**
     * Call on tracker page load. Returns a context object the tracker uses.
     * @param {string} trackerId  'feeding' | 'sleep' | 'baby-log'
     * @returns {Promise<{ ownerId: string|null, ownerName: string|null, isCaregiver: boolean }>}
     */
    init: function (trackerId) {
      return loadInvites().then(function () {
        var ownerId = getCaregiverOwner(trackerId);
        if (!ownerId) return { ownerId: null, ownerName: null, isCaregiver: false };
        var received = (_invites && _invites.received) ? _invites.received.find(function (r) { return r.ownerId === ownerId; }) : null;
        var ownerName = received ? received.ownerName : 'Your caregiver partner';
        showCaregiverBanner(ownerName, trackerId);
        return { ownerId: ownerId, ownerName: ownerName, isCaregiver: true };
      });
    },

    /** Open caregiver management panel. */
    openPanel: openPanel,

    /** Fetch tracker data (owner's if isCaregiver). */
    fetchData: fetchTrackerData,

    /** Push tracker data (owner's if isCaregiver). */
    pushData: pushTrackerData,

    /** Returns received invite list (for building caregiver switcher). */
    getReceivedInvites: function (trackerId) {
      if (!_invites) return [];
      return (_invites.received || []).filter(function (r) { return r.trackers.indexOf(trackerId) !== -1; });
    },

    /**
     * Returns all contexts the current user can view for a given tracker:
     * their own children + any shared (caregiver) children.
     * Each context: { type, childName, childId, dob, ownerId, ownerName, label }
     */
    getAllContexts: getAllContexts,
  };
})();
